import type { TAuthLoginResponsePayload } from "../api/auth/auth.types";
import { tokenStorage } from "./tokenStorage";
import { apiURL } from "./env";
import { getJwtExpiration } from "./jwt";

// Single in-flight refresh promise — all concurrent callers share it
let inflightRefresh: Promise<boolean> | null = null;

// Bumped on logout. A refresh that started before logout must not write tokens
// back after cookies were cleared — that silently resurrects the session.
let sessionEpoch = 0;

/**
 * Invalidate any in-flight/silent refresh. Call when the user logs out so a
 * refresh racing the logout cannot re-store fresh tokens.
 */
export function invalidateSessionForLogout(): void {
  sessionEpoch++;
  inflightRefresh = null;
}

/**
 * Attempts to silently refresh the access token using the stored refresh token.
 * Returns true if the refresh succeeded and new tokens are stored, false otherwise.
 * Multiple concurrent callers share the same in-flight request.
 */
export async function attemptSilentRefresh(): Promise<boolean> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return false;

  if (inflightRefresh) return inflightRefresh;

  const epochAtStart = sessionEpoch;

  inflightRefresh = (async (): Promise<boolean> => {
    try {
      const response = await fetch(apiURL("auth/refresh-token").toString(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) return false;

      const data = (await response.json()) as TAuthLoginResponsePayload;

      // The user logged out while this refresh was in flight — discard the result.
      if (epochAtStart !== sessionEpoch) return false;

      tokenStorage.setAccessToken(data.data.access_token);
      if (data.data.refresh_token?.token) {
        tokenStorage.setRefreshToken(data.data.refresh_token.token);
      }
      const expiration = getJwtExpiration(data.data.access_token);
      if (expiration !== null) tokenStorage.setExpiresAt(expiration);

      return true;
    } catch {
      return false;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

/**
 * Returns true if the stored access token expires within `thresholdMs` milliseconds.
 * Default threshold is 2 minutes — enough time for the proactive refresh to complete.
 */
export function isTokenExpiringSoon(thresholdMs = 120_000): boolean {
  const expiresAt = tokenStorage.getExpiresAt();
  if (!expiresAt) return false;
  return Date.now() > expiresAt - thresholdMs;
}
